import {CompositeFilterDescriptor, SortDescriptor} from "@progress/kendo-data-query"
import {Grid, GridCellProps, GridColumn, GridHeaderCellProps, GridSortChangeEvent} from "@progress/kendo-react-grid"
import {useInternationalization} from "@progress/kendo-react-intl"
import {Tooltip} from "@progress/kendo-react-tooltip"
import React, {useEffect, useState} from "react"
import {RootState} from "../../rootReducer"
import {SpinnerGrid} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {fetchRecurrenceSchedules, ReccurringEventsParam} from "../../containers/DVSASchedules/schedulesSlice"
import dayjs from "dayjs"
import {MDBIcon} from "mdbreact"

type ScheduleOccurencesProps = {
    startDate: Date
    dealerId: string
    eventType: string
    recurringGroup: string
    scheduleStart: Date
    scheduleEnd: Date
}

const SchedulerOccurrences = ({
    startDate,
    dealerId,
    eventType,
    recurringGroup,
    scheduleStart,
    scheduleEnd,
}: ScheduleOccurencesProps) => {
    const dispatch = useAppDispatch()
    const recurringSchedules = useAppSelector((state: RootState) => state.containers.schedules.recurrenceSchedules)
    const loading = useAppSelector((state: RootState) => state.containers.schedules.recurrenceLoading)
    const [translatedSchedules, setTranslatedSchedules] = useState([])

    useEffect(() => {
        if (dealerId && recurringGroup) {
            const data = {} as ReccurringEventsParam
            data.dealerId = dealerId
            data.recurringGroup = recurringGroup
            data.eventType = eventType
            data.fromDate = dayjs(startDate).format("YYYY-MM-DD")
            data.schStart = dayjs(scheduleStart).format("YYYY-MM-DDTHH:mm")
            data.schEnd = dayjs(scheduleEnd).format("YYYY-MM-DDTHH:mm")
            dispatch(fetchRecurrenceSchedules(data))
        }
    }, [dealerId, scheduleStart, scheduleEnd, dispatch, startDate])

    useEffect(() => {
        if (recurringSchedules && recurringSchedules.length > 0) {
            const newSchs: any = []
            recurringSchedules.forEach((schedule: any) => {
                const {
                    eventTitle,
                    startDateTime,
                    endDateTime,
                    bookedStart,
                    bookedEnd,
                    noConflicts,
                    bookings,
                    bookedDuration,
                    breakDuration,
                } = schedule
                newSchs.push({
                    eventTitle,
                    startDateTime: new Date(startDateTime),
                    endDateTime: new Date(endDateTime),
                    bookedStart: bookedStart !== null ? new Date(bookedStart) : null,
                    bookedEnd: bookedEnd !== null ? new Date(bookedEnd) : null,
                    bookedDuration,
                    breakDuration,
                    noConflicts,
                    bookings,
                })
            })
            setTranslatedSchedules(newSchs)
        }
    }, [recurringSchedules])

    const [page, setPage] = useState({
        skip: 15,
        take: 15,
    })

    const initialFilter: CompositeFilterDescriptor = {
        logic: "and",
        filters: [
            {
                field: "eventTitle",
                operator: "contains",
                value: "",
            },
        ],
    }

    const [filter, setFilter] = useState<CompositeFilterDescriptor>(initialFilter)
    const [sort, setSort] = useState<SortDescriptor[]>([])

    const pageChange = (e: any) => setPage(e.page)

    const DateFormatCell = (props: GridCellProps) => {
        const intl = useInternationalization()
        const dateVal =
            props.dataItem[`${props.field}`] == null || props.dataItem[`${props.field}`] === ""
                ? ""
                : intl.formatDate(new Date(props.dataItem[`${props.field}`]), "dd/MM/yyyy")
        return <td>{dateVal}</td>
    }

    const BookedSchCell = (props: GridCellProps) => {

        const bookingCount = parseInt(props.dataItem[`bookings`])

        const bookingDuration = props.dataItem[`bookedDuration`]
        const value = bookingCount > 0 ? `${bookingCount} bookings between ${bookingDuration}` : ""
        return (
            <td>
                <Tooltip openDelay={100} position="bottom" anchorElement="target">
                    <span title={value}>{value}</span>
                </Tooltip>
            </td>
        )
    }

    const NoConflictCell = (props: GridCellProps) => {
        return (
            <td className="text-center">
                {props.dataItem[`${props.field}`] ? (
                    <MDBIcon color="success" fas icon="check" />
                ) : (
                    <MDBIcon color="danger" fas icon="times" />
                )}
            </td>
        )
    }

    const BreakCheckCell = (props: GridCellProps) => {
        return (
            <td className="text-center">
                <MDBIcon color="success" fas icon="check" />
            </td>
        )
    }

    const DeleteCell = (props: GridCellProps) => {
        return (
            <td className="text-center">
                {props.dataItem[`${props.field}`] === 0 ? (
                    <MDBIcon color="success" fas icon="check" />
                ) : (
                    <MDBIcon color="danger" fas icon="times" />
                )}
            </td>
        )
    }
    const TextCenterHeaderCell = (props: GridHeaderCellProps) => (
        <div className="text-center">
            <span title={props.title}>{props.title}</span>
            {props.children}
        </div>
    )

    return (
        <div className="position-relative">
            {loading && <SpinnerGrid />}
            {translatedSchedules && (
                <Grid
                    style={{
                        height: "calc(100vh - 296px)",
                    }}
                    data={recurringSchedules}
                    skip={page.skip}
                    take={page.take}
                    pageSize={translatedSchedules.length}
                    total={translatedSchedules.length}
                    filter={filter}
                    onFilterChange={e => setFilter(e.filter)}
                    pageable={{
                        buttonCount: 0,
                        pageSizes: false,
                    }}
                    sortable={true}
                    sort={sort}
                    onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
                    navigatable={false}
                    onPageChange={pageChange}
                >
                    <GridColumn field="startDateTime" width={100} title="Date" cell={DateFormatCell} />
                    {eventType === "SCH" && <GridColumn field="eventTitle" width={120} title="Schedule" />}
                    {eventType === "SCH" && <GridColumn field="breakDuration" width={120} title="Break" />}
                    {eventType === "SCH" && <GridColumn field="bookedStart" title="Bookings" cell={BookedSchCell} />}

                    {eventType === "SCH" && (
                        <GridColumn
                            width={80}
                            field="noConflicts"
                            title="Edited"
                            cell={NoConflictCell}
                            headerCell={TextCenterHeaderCell}
                        />
                    )}
                    {eventType === "SCH" && (
                        <GridColumn
                            width={80}
                            field="bookings"
                            title="Deleted"
                            cell={DeleteCell}
                            headerCell={TextCenterHeaderCell}
                        />
                    )}
                    {eventType === "BR" && <GridColumn field="eventTitle" title="Break" />}
                    {eventType === "BR" && (
                        <GridColumn
                            width={80}
                            field="eventTitle"
                            title="Save"
                            cell={BreakCheckCell}
                            headerCell={TextCenterHeaderCell}
                        />
                    )}
                </Grid>
            )}
        </div>
    )
}
export default SchedulerOccurrences
